import React from 'react';
import type { ReviewCycle } from './types';
import { isYourStep, STEP_LABEL as SHARED_STEP_LABEL } from './reviewStage';

interface ReviewSequenceIndicatorProps {
  cycle: ReviewCycle;
  // Which step the current viewer acts at. Highlights their step with a
  // "You" pill so reviewers can find themselves in the sequence at a glance.
  currentUserStep: 'medical' | 'legal' | 'producer' | null;
  // Walk fix F11 (2026-08-12) — the viewer's email. The producer step's
  // YOU pill gates on an actual person match against cycle.producer:
  // useCurrentUserStep resolves DM/admins WITHOUT a reviewer context to
  // 'producer' (an edit-lock fallback), which made every admin viewing a
  // cycle see "YOU" on a step owned by a named colleague.
  currentUserEmail?: string | null;
}

// Re-exported from reviewStage so the dashboard's stage badges and this
// strip cannot drift apart on wording.
const STEP_LABEL: Record<string, string> = SHARED_STEP_LABEL;

/**
 * Phase 12 fix pass (Batch B) — sequence position awareness for the
 * reviewer view. Renders the cycle's reviewSequence as a small horizontal
 * strip: [Medical ✓] → [Legal (You)] → [Producer], with the next
 * uncompleted step highlighted and the current user's step marked "You".
 *
 * Doesn't render on legacy cycles (reviewSequence missing/empty). Purely
 * informational — no actions, no state.
 */
const ReviewSequenceIndicator: React.FC<ReviewSequenceIndicatorProps> = ({
  cycle, currentUserStep, currentUserEmail = null
}) => {
  const seq = cycle.reviewSequence || [];
  if (seq.length === 0) return null;
  const sorted = [...seq].sort((a, b) => a.order - b.order);
  const completed = new Set((cycle.stepCompletions || []).map(sc => sc.step));
  const nextStep = sorted.find(s => !completed.has(s.step));
  // F11 lives in reviewStage.isYourStep now — same rule, one copy, so the
  // dashboard's "your turn" grouping and this strip's YOU pill always agree.
  const isYouStep = (step: string) =>
    isYourStep(cycle, step as any, currentUserStep, currentUserEmail);

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '4px 8px',
        background: '#f4f6fa',
        borderRadius: 6,
        fontSize: 12,
        color: '#495057'
      }}
      title="Review sequence — the order in which reviewers act. Each step is completed by its owner before the next begins."
      data-testid="review-sequence-indicator"
    >
      {sorted.map((s, i) => {
        const isComplete = completed.has(s.step);
        const isNext = nextStep?.step === s.step;
        const isYou = isYouStep(s.step);
        return (
          <React.Fragment key={s.step}>
            {i > 0 && <span style={{ color: '#adb5bd' }}>→</span>}
            <span
              style={{
                padding: '2px 8px',
                borderRadius: 10,
                background: isComplete
                  ? '#d4edda'
                  : isNext
                    ? '#fff3cd'
                    : '#e9ecef',
                color: isComplete
                  ? '#155724'
                  : isNext
                    ? '#856404'
                    // 2026-08-26: #6c757d is 3.95:1 on this pill's #e9ecef at
                    // 12px — below AA. #55606d is 5.40:1 on the same ground.
                    : '#55606d',
                fontWeight: isNext ? 600 : 400,
                border: isNext ? '1px solid #ffc107' : '1px solid transparent'
              }}
            >
              {STEP_LABEL[s.step] || s.step}
              {isComplete && ' ✓'}
              {isYou && (
                <span
                  style={{
                    marginLeft: 4,
                    padding: '0 5px',
                    background: '#123250',
                    color: '#fff',
                    borderRadius: 8,
                    fontSize: 10,
                    fontWeight: 700
                  }}
                >
                  YOU
                </span>
              )}
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default ReviewSequenceIndicator;
